/**
 * Jeopardy's words over the generic pack/item routes; the server knows no game vocabulary.
 * Body types come from the game's Zod schemas, so a field rename breaks this at compile time.
 */

import { request, ApiError } from '@gsf/kit';
import {
  MAX_ROUND_NAME,
  correctAnswerText,
  defaultRoundName,
  faceValue,
  isDraftClue,
  legacyClueMode,
  type JeopardyCategoryBody,
  type JeopardyClueBody,
  type JeopardyClueMode,
  type JeopardyLockout,
  type JeopardyRoundBody,
  type JeopardySharedRound,
} from '@gsf/games';

/** One hit from the embedded clue search. */
export interface ClueSearchResult {
  id: string;
  prompt: string;
  answer: string;
  category: string;
}

/** A reusable pool question (#29), as the author page and the bank search edit it. */
export interface PoolQuestion {
  id: string;
  prompt: string;
  correctAnswer: string;
  incorrectAnswers: string[];
  difficulty: number;
  tags: string[];
}

export type QuestionMode = JeopardyClueMode;
export type LockoutMode = JeopardyLockout;
export type RoundView = JeopardySharedRound;

/** Adapter shapes for the admin components: a clue flattened for a form. */
export interface Category {
  id: string;
  name: string;
  round: number;
  position: number;
}

export interface Question {
  id: string;
  categoryId: string;
  prompt: string;
  answer: string;
  points: number;
  mode: QuestionMode;
  /** Default lockout for this clue; the host can still override at play time. */
  lockoutMode: LockoutMode;
  /**
   * Countdown for `open` mode, in seconds. Null (or 0) means no timer at all:
   * answers stay open until the host closes them by hand.
   */
  answerSeconds: number | null;
  position: number;
}

export interface CategoryWithQuestions extends Category {
  questions: Question[];
}

interface ContentItem<Body> {
  id: string;
  packId: string;
  kind: string;
  body: Body;
  schemaVersion: number;
  position: number;
  tags: string[];
}

interface ContentPack {
  id: string;
  gameId: string;
  title: string;
}

/**
 * Which pack the bank edits. A host screen points this at the running session's pack
 * (`setBankPack`), because a session can be pinned to a pack that is not the game's first;
 * the admin "question loader" leaves it null and edits the server's first jeopardy pack.
 */
let activePackId: string | null = null;

/** Point the bank at a session's pack, or `null` to fall back to the first jeopardy pack. */
export function setBankPack(packId: string | null): void {
  activePackId = packId;
}

/**
 * Fallback cache for the admin screen: the first jeopardy pack. Reset on failure so a load
 * during a server restart does not poison the tab.
 */
let packPromise: Promise<string> | null = null;

function jeopardyPackId(): Promise<string> {
  if (activePackId) return Promise.resolve(activePackId);
  packPromise ??= request<ContentPack[]>('/packs?gameId=jeopardy')
    .then((packs) => {
      const pack = packs[0];
      if (!pack) throw new ApiError('This server has no Jeopardy content pack.', 404);
      return pack.id;
    })
    .catch((err: unknown) => {
      packPromise = null;
      throw err;
    });
  return packPromise;
}

async function items<Body>(kind: string): Promise<Array<ContentItem<Body>>> {
  const pack = await jeopardyPackId();
  return request<Array<ContentItem<Body>>>(`/packs/${pack}/items?kind=${kind}`);
}

/**
 * The library row as the server sends it (`/api/questions`). `answer`/`categories` are the
 * storage names; the UI shape (`PoolQuestion`) calls them `correctAnswer`/`tags`.
 */
interface QuestionRow {
  id: string;
  prompt: string;
  answer: string;
  incorrectAnswers: string[];
  categories: string[];
  difficulty: number;
}

function toPoolQuestion(row: QuestionRow): PoolQuestion {
  return {
    id: row.id,
    prompt: row.prompt,
    correctAnswer: row.answer,
    incorrectAnswers: row.incorrectAnswers,
    difficulty: row.difficulty,
    tags: row.categories,
  };
}

function questionPayload(input: {
  prompt: string;
  correctAnswer: string;
  incorrectAnswers: string[];
  difficulty: number;
  tags: string[];
}): string {
  return JSON.stringify({
    prompt: input.prompt.trim(),
    answer: input.correctAnswer.trim(),
    incorrectAnswers: input.incorrectAnswers.map((a) => a.trim()).filter((a) => a !== ''),
    categories: input.tags,
    difficulty: input.difficulty,
  });
}

/**
 * The flat fields the `ClueEditor` grid edits, derived from a v2 body. Round-trips through
 * `legacyToBody`.
 */
function bodyToLegacy(body: JeopardyClueBody): Omit<Question, 'id'> {
  return {
    categoryId: body.categoryId,
    prompt: body.prompt,
    answer: correctAnswerText(body),
    points: faceValue(body),
    mode: legacyClueMode(body),
    // `lockoutMode` in the components because `mode` already means how the clue is
    // played.
    lockoutMode: body.lockout,
    answerSeconds: body.answerSeconds,
    position: body.position,
  };
}

/** A clue item as the old `questions` row the components still expect. */
function toQuestion(item: ContentItem<JeopardyClueBody>): Question {
  return { id: item.id, ...bodyToLegacy(item.body) };
}

/**
 * The three modes back to a v2 body: `buzzer`→`verbal`+static, `open`→`textInput`+static,
 * `daily_double`→`verbal`+accrued.
 */
function legacyToBody(q: Omit<Question, 'id'>): JeopardyClueBody {
  const base = {
    categoryId: q.categoryId,
    prompt: q.prompt,
    position: q.position,
    tileDisplay: { kind: 'points' } as const,
    lockout: q.lockoutMode,
    answerSeconds: q.answerSeconds,
  };
  if (q.mode === 'daily_double') {
    return { questionType: 'verbal', correctAnswer: q.answer, scoring: { kind: 'accrued', maxWager: q.points }, ...base };
  }
  if (q.mode === 'open') {
    return { questionType: 'textInput', correctAnswer: q.answer, scoring: { kind: 'static', points: q.points }, ...base };
  }
  return { questionType: 'verbal', correctAnswer: q.answer, scoring: { kind: 'static', points: q.points }, ...base };
}

function toCategory(item: ContentItem<JeopardyCategoryBody>): Category {
  return {
    id: item.id,
    name: item.body.name,
    round: item.body.round,
    position: item.body.position,
  };
}

/**
 * Ids compare as text: converted ids are zero-padded and new ones are minted time-first,
 * so a string compare preserves numeric order.
 */
function byId(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Categories with their clues, in the board's order, including the empty columns a board
 * leaves out. Hosts arrange this order by hand.
 */
async function listBank(round?: number): Promise<CategoryWithQuestions[]> {
  const [categoryItems, clueItems] = await Promise.all([
    items<JeopardyCategoryBody>('category'),
    items<JeopardyClueBody>('clue'),
  ]);

  const byCategory = new Map<string, Question[]>();
  for (const item of clueItems) {
    const question = toQuestion(item);
    const list = byCategory.get(question.categoryId);
    if (list) list.push(question);
    else byCategory.set(question.categoryId, [question]);
  }

  return categoryItems
    .map(toCategory)
    .filter((category) => round === undefined || category.round === round)
    .sort((a, b) => a.round - b.round || a.position - b.position || byId(a.id, b.id))
    .map((category) => ({
      ...category,
      questions: (byCategory.get(category.id) ?? []).sort(
        (a, b) => a.position - b.position || a.points - b.points || byId(a.id, b.id),
      ),
    }));
}

/**
 * Rounds exist because a category is in them; unnamed ones fall back to "Round N". Must
 * match `jeopardyRounds`: the host screen reads tabs from the snapshot, the admin from here.
 */
async function listRounds(): Promise<RoundView[]> {
  const [categoryItems, roundItems] = await Promise.all([
    items<JeopardyCategoryBody>('category'),
    items<JeopardyRoundBody>('round'),
  ]);

  const names = new Map(roundItems.map((item) => [item.body.round, item.body.name]));
  const rounds = new Set(categoryItems.map((item) => item.body.round));

  return [...rounds]
    .sort((a, b) => a - b)
    .map((round) => ({ round, name: names.get(round) ?? defaultRoundName(round) }));
}

async function createItem<Body>(kind: string, body: Body): Promise<ContentItem<Body>> {
  const pack = await jeopardyPackId();
  return request<ContentItem<Body>>(`/packs/${pack}/items`, {
    method: 'POST',
    body: JSON.stringify({ kind, body }),
  });
}

async function patchBody<Body>(id: string, body: Body): Promise<ContentItem<Body>> {
  return request<ContentItem<Body>>(`/items/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ body }),
  });
}

function deleteItems(ids: string[]): Promise<void> {
  return request<void>(`/items?ids=${ids.join(',')}`, { method: 'DELETE' });
}

/** Read the current body so a partial edit can be merged before it is validated. */
async function getBody<Body>(id: string): Promise<Body> {
  return (await request<ContentItem<Body>>(`/items/${id}`)).body;
}

export const bank = {
  listBank,
  listRounds,

  /**
   * Name a round, or clear the name with an empty string. Clearing deletes the item rather
   * than storing "Round 3".
   */
  renameRound: async (round: number, name: string): Promise<void> => {
    const trimmed = name.trim().slice(0, MAX_ROUND_NAME);
    const existing = (await items<JeopardyRoundBody>('round')).find(
      (item) => item.body.round === round,
    );
    // The round item may also carry a wager pool, so a rename must not drop it, and
    // clearing the name deletes the item only when nothing else is on it.
    const pool = existing?.body.wagerPool;

    if (trimmed === '') {
      if (existing && pool) await patchBody<JeopardyRoundBody>(existing.id, { round, wagerPool: pool });
      else if (existing) await deleteItems([existing.id]);
      return;
    }
    if (existing) {
      await patchBody<JeopardyRoundBody>(existing.id, {
        round,
        name: trimmed,
        ...(pool ? { wagerPool: pool } : {}),
      });
    } else await createItem<JeopardyRoundBody>('round', { round, name: trimmed });
  },

  /** This round's wager pool values, or `[]` when it has none. */
  getWagerPool: async (round: number): Promise<number[]> => {
    const existing = (await items<JeopardyRoundBody>('round')).find(
      (item) => item.body.round === round,
    );
    return existing?.body.wagerPool ? [...existing.body.wagerPool.values] : [];
  },

  /**
   * An empty `values` clears the pool. The round name is preserved either way: the two
   * share one round item.
   */
  saveWagerPool: async (round: number, values: number[]): Promise<void> => {
    const existing = (await items<JeopardyRoundBody>('round')).find(
      (item) => item.body.round === round,
    );
    const name = existing?.body.name;

    if (values.length === 0) {
      if (existing && name) await patchBody<JeopardyRoundBody>(existing.id, { round, name });
      else if (existing) await deleteItems([existing.id]);
      return;
    }
    const body: JeopardyRoundBody = { round, wagerPool: { values }, ...(name ? { name } : {}) };
    if (existing) await patchBody<JeopardyRoundBody>(existing.id, body);
    else await createItem<JeopardyRoundBody>('round', body);
  },

  createCategory: async (input: { name: string; round: number }): Promise<Category> => {
    // Position within the round; the generic default is per-pack-per-kind and would put
    // a new round-2 column after every round-1 column.
    const inRound = (await listBank(input.round)).length;
    return toCategory(
      await createItem<JeopardyCategoryBody>('category', {
        name: input.name.trim(),
        round: input.round,
        position: inRound,
      }),
    );
  },

  updateCategory: async (
    id: string,
    input: Partial<{ name: string; round: number; position: number }>,
  ): Promise<Category> => {
    const body = await getBody<JeopardyCategoryBody>(id);
    return toCategory(
      await patchBody<JeopardyCategoryBody>(id, {
        name: input.name?.trim() ?? body.name,
        round: input.round ?? body.round,
        position: input.position ?? body.position,
      }),
    );
  },

  /**
   * Deletes a column and its clues as one bulk request, because two requests can half
   * fail and leave clues with no column.
   */
  deleteCategory: async (id: string): Promise<void> => {
    const clues = (await items<JeopardyClueBody>('clue'))
      .filter((item) => item.body.categoryId === id)
      .map((item) => item.id);
    await deleteItems([id, ...clues]);
  },

  /**
   * Every clue in a round with its v2 body and tier index (its row on screen, in the
   * board's order), for the round editor. One pair of reads rather than N `getClueBody`.
   */
  roundClueBodies: async (
    round: number,
  ): Promise<Array<{ id: string; tierIndex: number; body: JeopardyClueBody }>> => {
    const [categoryItems, clueItems] = await Promise.all([
      items<JeopardyCategoryBody>('category'),
      items<JeopardyClueBody>('clue'),
    ]);
    const inRound = new Set(
      categoryItems.filter((c) => c.body.round === round).map((c) => c.id),
    );
    const byCategory = new Map<string, Array<ContentItem<JeopardyClueBody>>>();
    for (const item of clueItems) {
      if (!inRound.has(item.body.categoryId)) continue;
      const list = byCategory.get(item.body.categoryId);
      if (list) list.push(item);
      else byCategory.set(item.body.categoryId, [item]);
    }
    const out: Array<{ id: string; tierIndex: number; body: JeopardyClueBody }> = [];
    for (const list of byCategory.values()) {
      list
        .sort((a, b) => a.body.position - b.body.position || faceValue(a.body) - faceValue(b.body) || byId(a.id, b.id))
        .forEach((item, tierIndex) => out.push({ id: item.id, tierIndex, body: item.body }));
    }
    return out;
  },

  getQuestion: async (id: string): Promise<Question> =>
    toQuestion(await request<ContentItem<JeopardyClueBody>>(`/items/${id}`)),

  createQuestion: async (input: {
    categoryId: string;
    prompt: string;
    answer: string;
    points?: number;
    mode?: QuestionMode;
    lockoutMode?: LockoutMode;
    answerSeconds?: number | null;
  }): Promise<Question> => {
    // Position within the column.
    const siblings = (await items<JeopardyClueBody>('clue')).filter(
      (item) => item.body.categoryId === input.categoryId,
    );
    return toQuestion(
      await createItem<JeopardyClueBody>(
        'clue',
        legacyToBody({
          categoryId: input.categoryId,
          prompt: input.prompt.trim(),
          answer: input.answer.trim(),
          points: input.points ?? 100,
          mode: input.mode ?? 'buzzer',
          lockoutMode: input.lockoutMode ?? 'host_enable',
          answerSeconds: input.answerSeconds ?? null,
          position: siblings.length,
        }),
      ),
    );
  },

  /**
   * Merge and write. The server will not merge a JSON body: it cannot tell "leave
   * `answerSeconds`" from "clear it", so the read happens here, where fields have names.
   */
  updateQuestion: async (
    id: string,
    input: Partial<{
      categoryId: string;
      prompt: string;
      answer: string;
      points: number;
      mode: QuestionMode;
      lockoutMode: LockoutMode;
      answerSeconds: number | null;
      position: number;
    }>,
  ): Promise<Question> => {
    const current = bodyToLegacy(await getBody<JeopardyClueBody>(id));
    return toQuestion(
      await patchBody<JeopardyClueBody>(
        id,
        legacyToBody({
          categoryId: input.categoryId ?? current.categoryId,
          prompt: input.prompt?.trim() ?? current.prompt,
          answer: input.answer?.trim() ?? current.answer,
          points: input.points ?? current.points,
          mode: input.mode ?? current.mode,
          lockoutMode: input.lockoutMode ?? current.lockoutMode,
          answerSeconds:
            input.answerSeconds === undefined ? current.answerSeconds : input.answerSeconds,
          position: input.position ?? current.position,
        }),
      ),
    );
  },

  /** The full v2 clue body for the rich editor; `getQuestion` flattens to the legacy shape. */
  getClueBody: (id: string): Promise<JeopardyClueBody> => getBody<JeopardyClueBody>(id),

  /** Write a whole v2 clue body. The server re-broadcasts to any room reading the pack. */
  saveClueBody: async (id: string, body: JeopardyClueBody): Promise<void> => {
    await patchBody<JeopardyClueBody>(id, body);
  },

  /**
   * Substring match over written clues on prompt, answer and category. Drafts are skipped:
   * the host is looking for something to reuse.
   */
  searchClues: async (query: string): Promise<ClueSearchResult[]> => {
    const q = query.trim().toLowerCase();
    const [categories, clues] = await Promise.all([
      items<JeopardyCategoryBody>('category'),
      items<JeopardyClueBody>('clue'),
    ]);
    const categoryName = new Map(categories.map((c) => [c.id, c.body.name]));
    return clues
      .filter((c) => !isDraftClue(c.body))
      .map((c) => ({
        id: c.id,
        prompt: c.body.prompt,
        answer: correctAnswerText(c.body),
        category: categoryName.get(c.body.categoryId) ?? '',
      }))
      .filter(
        (r) =>
          q === '' ||
          r.prompt.toLowerCase().includes(q) ||
          r.answer.toLowerCase().includes(q) ||
          r.category.toLowerCase().includes(q),
      )
      .slice(0, 50);
  },

  deleteQuestion: (id: string): Promise<void> => deleteItems([id]),

  /** Bulk because two requests can half fail. */
  deleteQuestions: (ids: string[]): Promise<void> =>
    ids.length === 0 ? Promise.resolve() : deleteItems(ids),

  // -------------------------------------------------------------------------
  // The reusable question pool (#29): `question` items in the account's bank pack.
  // Named `*PoolQuestion` to sit beside the clue `createQuestion` / `deleteQuestion` above.
  // -------------------------------------------------------------------------

  listQuestions: async (): Promise<PoolQuestion[]> => {
    const rows = await request<QuestionRow[]>('/questions');
    return rows.map(toPoolQuestion);
  },

  createPoolQuestion: async (input: {
    prompt: string;
    correctAnswer: string;
    incorrectAnswers: string[];
    difficulty: number;
    tags: string[];
  }): Promise<PoolQuestion> => {
    const created = await request<QuestionRow>('/questions', {
      method: 'POST',
      body: questionPayload(input),
    });
    return toPoolQuestion(created);
  },

  updatePoolQuestion: async (
    id: string,
    input: {
      prompt: string;
      correctAnswer: string;
      incorrectAnswers: string[];
      difficulty: number;
      tags: string[];
    },
  ): Promise<PoolQuestion> => {
    const updated = await request<QuestionRow>(`/questions/${id}`, {
      method: 'PATCH',
      body: questionPayload(input),
    });
    return toPoolQuestion(updated);
  },

  deletePoolQuestion: (id: string): Promise<void> =>
    request<void>(`/questions/${id}`, { method: 'DELETE' }),

  /**
   * Substring match over the pool a board may draw from (own + shared), on prompt, answer and
   * tags, for the clue editor's import.
   */
  searchQuestions: async (query: string): Promise<PoolQuestion[]> => {
    const q = query.trim().toLowerCase();
    const rows = await request<QuestionRow[]>('/questions?available=1');
    return rows
      .map(toPoolQuestion)
      .filter((r) => r.prompt.trim() !== '' || r.correctAnswer.trim() !== '')
      .filter(
        (r) =>
          q === '' ||
          r.prompt.toLowerCase().includes(q) ||
          r.correctAnswer.toLowerCase().includes(q) ||
          r.tags.some((t) => t.toLowerCase().includes(q)),
      )
      .slice(0, 50);
  },
};

/** Prefill for the timer field when writing an open-answer clue. Not a fallback. */
export const DEFAULT_OPEN_ANSWER_SECONDS = 30;
